import { useEffect, useState, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import * as turf from '@turf/turf';

export default function OverlapLayer({ features = [] }) {
  const map = useMap();
  const [isFlashing, setIsFlashing] = useState(false);
  const dataLayerRef = useRef(null);

  // Initialize the dedicated Data layer for overlaps
  useEffect(() => {
    if (!map || dataLayerRef.current) return;

    dataLayerRef.current = new window.google.maps.Data({ map });
    dataLayerRef.current.setStyle({
      fillColor: '#8b0000', // Dark Red
      fillOpacity: 0, // Starts hidden, updated by interval
      strokeColor: '#ff0000',
      strokeWeight: 2,
      clickable: false, // Don't block clicks on the actual areas
      zIndex: 9999 // Ensure it renders on top
    });

    return () => {
      if (dataLayerRef.current) {
        dataLayerRef.current.setMap(null);
        dataLayerRef.current = null;
      }
    };
  }, [map]);

  // Calculate overlaps when features change (deferred to avoid blocking UI)
  useEffect(() => {
    if (!dataLayerRef.current) return;

    // Clear previous overlaps
    dataLayerRef.current.forEach((feature) => {
      dataLayerRef.current.remove(feature);
    });

    // Only process Polygons and MultiPolygons with assigned teams
    const validFeatures = features.filter(f => 
      f.geometry && 
      (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon') &&
      f.properties && f.properties.team_id
    );

    if (validFeatures.length < 2) return;

    const timer = setTimeout(() => {
      if (!dataLayerRef.current) return;

      // Precompute bounding boxes for ultra-fast AABB rejection
      const bboxes = validFeatures.map(f => {
        try {
          return turf.bbox(f);
        } catch {
          return null;
        }
      });

      // Compare pairs with different teams
      const overlaps = [];
      for (let i = 0; i < validFeatures.length; i++) {
        const b1 = bboxes[i];
        if (!b1) continue;
        const f1 = validFeatures[i];

        for (let j = i + 1; j < validFeatures.length; j++) {
          const f2 = validFeatures[j];
          if (f1.properties.team_id === f2.properties.team_id) continue;

          const b2 = bboxes[j];
          if (!b2) continue;

          // Fast Bounding Box (AABB) intersection check: [minX, minY, maxX, maxY]
          // If bounding boxes do not overlap, polygons cannot intersect!
          if (b1[0] > b2[2] || b1[2] < b2[0] || b1[1] > b2[3] || b1[3] < b2[1]) {
            continue;
          }

          try {
            const intersection = turf.intersect(
              turf.featureCollection([
                turf.feature(f1.geometry), 
                turf.feature(f2.geometry)
              ])
            );
            
            if (intersection) {
              overlaps.push(intersection);
            }
          } catch (err) {
            console.error("Turf intersection error:", err);
          }
        }
      }

      // Add overlaps to the dedicated layer
      if (overlaps.length > 0 && dataLayerRef.current) {
        dataLayerRef.current.addGeoJson({
          type: 'FeatureCollection',
          features: overlaps
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [features, map]);

  // Flashing interval
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlashing(prev => !prev);
    }, 600); // 600ms toggle

    return () => clearInterval(interval);
  }, []);

  // Apply flashing style
  useEffect(() => {
    if (!dataLayerRef.current) return;
    
    dataLayerRef.current.setStyle({
      fillColor: '#8b0000', // Dark Red
      fillOpacity: isFlashing ? 0.7 : 0.1, // Toggle opacity
      strokeColor: '#ff0000',
      strokeWeight: isFlashing ? 3 : 1,
      clickable: false,
      zIndex: 9999
    });
  }, [isFlashing]);

  return null;
}
